import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listPluginLogs,
  getPluginLogsStats,
  type PaginatedPluginLogs,
  type PluginLogsStats,
} from "@/services/pluginLogsApi";
import { SeverityBadge } from "./severity";
import { severityLabel } from "./severityLabel";
import { PluginLogDetailSheet } from "./PluginLogDetailSheet";

// Sentinelle "tous" (Radix Select interdit une value vide).
const ALL = "all";
const SEVERITY_OPTIONS = ["error", "warning", "transient"] as const;

function errMsg(e: unknown, fallback = "Erreur"): string {
  return e instanceof Error ? e.message : fallback;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function PluginLogsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedPluginLogs | null>(null);
  const [stats, setStats] = useState<PluginLogsStats | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Filtres
  const [severity, setSeverity] = useState<string>(ALL);
  const [workerInput, setWorkerInput] = useState("");
  const [sourceInput, setSourceInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [qInput, setQInput] = useState("");
  const [applied, setApplied] = useState({
    worker: "",
    source: "",
    reason: "",
    q: "",
  });

  // Debounce des champs texte (350ms) → reset page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied({
        worker: workerInput.trim(),
        source: sourceInput.trim(),
        reason: reasonInput.trim(),
        q: qInput.trim(),
      });
    }, 350);
    return () => clearTimeout(t);
  }, [workerInput, sourceInput, reasonInput, qInput]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listPluginLogs(navigate, {
          limit: size,
          offset: (page - 1) * size,
          severity: severity === ALL ? undefined : severity,
          worker_id: applied.worker || undefined,
          source: applied.source || undefined,
          reason: applied.reason || undefined,
          q: applied.q || undefined,
        });
        setData(res);
      } catch (e: unknown) {
        setError(errMsg(e, "Erreur inconnue"));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, severity, applied]);

  // Stats best-effort (fenêtre 7j, indépendantes des filtres).
  useEffect(() => {
    (async () => {
      try {
        setStats(await getPluginLogsStats(navigate, 7));
      } catch {
        setStats(null);
      }
    })();
  }, [navigate]);

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  const canPrev = page > 1;
  const canNext = page < pages;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Logs plugins</h1>
          </div>
        </header>

        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Erreurs worker / plugin</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Taille page</label>
              <select
                className="rounded border px-2 py-1 text-sm"
                value={size}
                onChange={(e) => {
                  setPage(1);
                  setSize(parseInt(e.target.value, 10));
                }}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bandeau statistiques (fenêtre 7 jours) */}
          {stats && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded border bg-card p-4">
                <div className="text-xs uppercase text-gray-500">
                  Total (7 j)
                </div>
                <div className="text-2xl font-semibold">{stats.total}</div>
              </div>
              {SEVERITY_OPTIONS.map((s) => {
                const b = stats.by_severity.find((x) => x.key === s);
                return (
                  <div key={s} className="rounded border bg-card p-4">
                    <div className="text-xs uppercase text-gray-500">
                      {severityLabel(s)}
                    </div>
                    <div className="text-2xl font-semibold">{b?.count ?? 0}</div>
                  </div>
                );
              })}
              {stats.by_reason.length > 0 && (
                <div className="col-span-2 rounded border bg-card p-4 sm:col-span-4">
                  <div className="mb-1 text-xs uppercase text-gray-500">
                    Top motifs (7 j)
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {stats.by_reason.slice(0, 8).map((r) => (
                      <button
                        key={r.key}
                        onClick={() => setReasonInput(r.key)}
                        className="rounded bg-muted px-2 py-0.5 font-mono hover:bg-muted/70"
                        title="Filtrer sur ce motif"
                      >
                        {r.key} · {r.count}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Barre de filtres */}
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={severity}
              onValueChange={(v) => {
                setPage(1);
                setSeverity(v);
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Sévérité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toutes les sévérités</SelectItem>
                {SEVERITY_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {severityLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="w-[200px]"
              placeholder="Worker ID"
              value={workerInput}
              onChange={(e) => setWorkerInput(e.target.value)}
            />
            <Input
              className="w-[180px]"
              placeholder="Source"
              value={sourceInput}
              onChange={(e) => setSourceInput(e.target.value)}
            />
            <Input
              className="w-[180px]"
              placeholder="Motif (reason)"
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
            />
            <Input
              className="w-[240px]"
              placeholder="Rechercher (message)…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
          {loading && <div className="text-sm text-gray-500">Chargement…</div>}

          <div className="overflow-auto rounded border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Worker</th>
                  <th className="px-3 py-2 text-left">Source / motif</th>
                  <th className="px-3 py-2 text-left">Sévérité</th>
                  <th className="px-3 py-2 text-left">Message</th>
                  <th className="px-3 py-2 text-left">Abo.</th>
                  <th className="px-3 py-2 text-left">Version</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((log) => (
                  <tr
                    key={log.id}
                    className="cursor-pointer border-t hover:bg-muted/50"
                    onClick={() => setSelectedId(log.id)}
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      {fmtDate(log.created_at)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {log.worker_id ? `${log.worker_id.substring(0, 12)}…` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <code className="text-xs">
                        {log.source}/{log.reason}
                      </code>
                    </td>
                    <td className="px-3 py-2">
                      <SeverityBadge severity={log.severity} />
                    </td>
                    <td
                      className="max-w-[360px] truncate px-3 py-2 text-xs text-muted-foreground"
                      title={log.message ?? undefined}
                    >
                      {log.message || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {log.subscription_id ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {log.plugin_version || "—"}
                    </td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr>
                    <td
                      className="px-3 py-4 text-center text-gray-500"
                      colSpan={7}
                    >
                      Aucun log
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {page} / {pages} — {total} élément(s)
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </div>

        <PluginLogDetailSheet
          logId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      </SidebarInset>
    </SidebarProvider>
  );
}
